/**
 * AdvisoryBlock — reusable "คำแนะนำก่อนยื่น" + "ข้อควรระวัง / สาเหตุถูกตีกลับ"
 * block required on every content page by the A5 content standard.
 *
 * Presentation only. Renders visible DOM that AI answer engines can lift
 * (no hidden text, no accordions) and carries `.advisory-tips` /
 * `.advisory-warnings` selectors used by our speakable/AEO audits.
 */
type AdvisoryBlockProps = {
  /** Actionable preparation advice — aim for >= 5 items. */
  tips: readonly string[];
  /** Rejection causes / things to avoid — aim for >= 5 items. */
  warnings: readonly string[];
  tipsHeading?: string;
  warningsHeading?: string;
  /** Optional freshness line, e.g. "ข้อมูล ณ สิงหาคม 2569". */
  reviewedNote?: string;
  className?: string;
};

export function AdvisoryBlock({
  tips,
  warnings,
  tipsHeading = "คำแนะนำก่อนยื่นเอกสาร",
  warningsHeading = "ข้อควรระวัง — สาเหตุที่เอกสารถูกตีกลับบ่อย",
  reviewedNote,
  className,
}: AdvisoryBlockProps) {
  return (
    <section
      className={`not-prose my-8 grid gap-4 md:grid-cols-2${className ? ` ${className}` : ""}`}
      aria-label={`${tipsHeading} และ ${warningsHeading}`}
    >
      <div className="advisory-tips rounded-xl border border-border bg-card/60 p-5">
        <h2 className="mb-3 text-lg font-semibold text-foreground">{tipsHeading}</h2>
        <ul className="list-disc space-y-2 pl-5 text-sm leading-relaxed text-muted-foreground">
          {tips.map((t) => (
            <li key={t}>{t}</li>
          ))}
        </ul>
      </div>
      <div className="advisory-warnings rounded-xl border border-destructive/30 bg-destructive/5 p-5">
        <h2 className="mb-3 text-lg font-semibold text-foreground">{warningsHeading}</h2>
        <ul className="list-disc space-y-2 pl-5 text-sm leading-relaxed text-muted-foreground">
          {warnings.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
        {reviewedNote ? (
          <p className="mt-3 text-xs text-muted-foreground">{reviewedNote}</p>
        ) : null}
      </div>
    </section>
  );
}
